import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dealsApi, importExportApi } from '../api';
import { Plus, Pencil, Trash2, Building2, Upload, Download } from 'lucide-react';
import DealModal from '../components/DealModal';
import ImportModal from '../components/ImportModal';

const STAGE_LABELS: Record<string, string> = {
  LEAD: 'Новые', NEGOTIATION: 'Переговоры', PROPOSAL: 'Предложение',
  AGREEMENT: 'Согласование', WON: 'Успешно', LOST: 'Проиграна',
};

const STAGE_COLORS: Record<string, { header: string; bg: string; count: string }> = {
  LEAD:       { header: '#e0f2fe', bg: '#f0f9ff', count: '#bae6fd' },
  NEGOTIATION: { header: '#fef3c7', bg: '#fffbeb', count: '#fde68a' },
  PROPOSAL:   { header: '#ede9fe', bg: '#f5f3ff', count: '#ddd6fe' },
  AGREEMENT:  { header: '#ccfbf1', bg: '#f0fdfa', count: '#99f6e4' },
  WON:        { header: '#dcfce7', bg: '#f0fdf4', count: '#bbf7d0' },
  LOST:       { header: '#fee2e2', bg: '#fef2f2', count: '#fecaca' },
};

export default function DealsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<'kanban' | 'table'>('kanban');
  const [deals, setDeals] = useState<any[]>([]);
  const [kanban, setKanban] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      if (view === 'kanban') {
        const res = await dealsApi.getKanban();
        setKanban(res.data);
      } else {
        const res = await dealsApi.list({ search: search || undefined, page, limit });
        setDeals(res.data.data);
        setTotal(res.data.total);
      }
    } catch (e) {
      console.error('Load deals error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, page, view]);
  useEffect(() => { setPage(1); }, [search, view]);

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить сделку?')) return;
    try { await dealsApi.remove(id); load(); } catch (e) { alert('Ошибка удаления'); }
  };

  const handleExport = async () => {
    try {
      const res = await importExportApi.exportDeals();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const d = new Date();
      a.download = `deals_${d.toISOString().slice(0, 10)}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка экспорта');
    }
  };

  const stageBadge = (stage: string) => {
    const colors: Record<string, string> = {
      LEAD: 'badge-info', NEGOTIATION: 'badge-warning', PROPOSAL: 'badge-warning',
      AGREEMENT: 'badge-default', WON: 'badge-success', LOST: 'badge-danger',
    };
    return <span className={`badge ${colors[stage] || 'badge-default'}`}>{STAGE_LABELS[stage] || stage}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Сделки</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setView(v => v === 'kanban' ? 'table' : 'kanban')}>
            {view === 'kanban' ? 'Вид: Список' : 'Вид: Канбан'}
          </button>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={18} /> Импорт
          </button>
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={18} /> Экспорт
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
            <Plus size={18} /> Добавить сделку
          </button>
        </div>
      </div>

      {view === 'table' && (
        <div className="card">
          <div className="card-header">
            <div className="toolbar" style={{ margin: 0, width: '100%' }}>
              <input className="search-input" placeholder="Поиск по сделкам..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loading ? <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div> : (
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>Название</th><th>Компания</th><th>Стадия</th><th>Сумма</th><th>Вероятность</th><th>Действия</th></tr></thead>
                  <tbody>
                    {deals.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Нет сделок</td></tr>}
                    {deals.map(d => (
                      <tr key={d.id}>
                        <td style={{ fontWeight: 600 }}><Link to={`/deals/${d.id}`} className="detail-link">{d.title}</Link></td>
                        <td className="text-muted">{d.company?.name || '—'}</td>
                        <td>{stageBadge(d.stage)}</td>
                        <td style={{ fontWeight: 500 }}>{d.amount ? Number(d.amount).toLocaleString('ru-RU') + ' \u20BD' : '—'}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div style={{ width: 40, background: '#eee', height: 4, borderRadius: 2 }}>
                              <div style={{ width: `${d.probability}%`, background: 'var(--color-primary)', height: 4, borderRadius: 2 }} />
                            </div>
                            <span style={{ fontSize: 12 }}>{d.probability}%</span>
                          </div>
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => { setEditing(d); setShowModal(true); }}>
                              <Pencil size={14} />
                            </button>
                            <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDelete(d.id)}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-soft)' }}>
            <div className="pagination">
              <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
              <div className="flex gap-2" style={{ margin: '0 16px' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>Страница {page}</span>
              </div>
              <button className="btn btn-secondary btn-sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Далее</button>
            </div>
          </div>
        </div>
      )}

      {view === 'kanban' && (
        <div className="kanban-board">
          {loading ? <div style={{ padding: 40 }}>Загрузка...</div> : kanban.map(col => {
            const colors = STAGE_COLORS[col.stage] || { header: '#f1f5f9', bg: '#f8fafc', count: '#e2e8f0' };
            return (
            <div className="kanban-column" key={col.stage} style={{ background: colors.bg }}>
              <div className="kanban-column-header" style={{ background: colors.header }}>
                <div className="kanban-column-title">
                  {col.title}
                  <span className="kanban-column-count" style={{ background: colors.count }}>{col.deals.length}</span>
                </div>
                <button className="btn-ghost" style={{ padding: 4 }}><Plus size={14} /></button>
              </div>
              <div className="kanban-column-body">
                {col.deals.map((d: any) => (
                  <div className="kanban-card" key={d.id} onClick={() => navigate(`/deals/${d.id}`)}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{d.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Building2 size={12} /> {d.company?.name || '—'}
                    </div>
                    {d.amount && (
                      <div style={{ 
                        fontSize: 13, 
                        marginTop: 12, 
                        fontWeight: 700, 
                        color: 'var(--color-text)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        {Number(d.amount).toLocaleString('ru-RU') + ' \u20BD'}
                        <div style={{ 
                          width: 24, height: 24, borderRadius: '50%', 
                          background: 'var(--color-primary-soft)', color: 'var(--color-primary)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10
                        }}>
                          {d.probability}%
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {col.deals.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12, border: '2px dashed #e2e8f0', borderRadius: 8 }}>
                    Пусто
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <DealModal deal={editing} onClose={() => setShowModal(false)} onSave={() => { setShowModal(false); load(); }} />
      )}

      {showImport && (
        <ImportModal
          entityType="deals"
          onImport={(file) => importExportApi.importDeals(file)}
          onClose={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}
